import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { BackLink } from "@/components/brand/BackLink";
import { CalculatorDisclaimer } from "@/components/brand/CalculatorDisclaimer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  fmtUSD,
  fmtMonthly,
  emailShareLink,
  logCalculatorCompletion,
} from "@/lib/calculators";

export const Route = createFileRoute("/tools/retirement")({
  head: () => ({
    meta: [
      { title: "Retirement Income Calculator — Will the Paycheck Keep Coming? | Currency Thoughts" },
      {
        name: "description",
        content:
          "Retirement isn't an age. It's a paycheck that doesn't depend on you working. See whether yours is on track.",
      },
      { property: "og:title", content: "Retirement Income — Will the paycheck keep coming?" },
      {
        property: "og:description",
        content:
          "The three buckets of retirement: Social Security, savings drawdown, and protected income.",
      },
    ],
    links: [{ rel: "canonical", href: "/tools/retirement" }],
  }),
  component: RetirementPage,
});

interface RState {
  currentAge: number;
  retireAge: number;
  currentSavings: number;
  monthlyContribution: number;
  preReturn: number; // %
  postReturn: number; // %
  desiredMonthly: number;
  socialSecurityMonthly: number;
  retirementYears: number;
}

function project(r: RState) {
  const yearsToRetire = Math.max(0, r.retireAge - r.currentAge);
  const monthsToRetire = yearsToRetire * 12;
  const mPre = r.preReturn / 100 / 12;
  let bal = r.currentSavings;
  for (let i = 0; i < monthsToRetire; i++) {
    bal = (bal + r.monthlyContribution) * (1 + mPre);
  }
  const nestEgg = bal;

  // Sustainable monthly draw from nest egg over retirementYears at postReturn
  const mPost = r.postReturn / 100 / 12;
  const n = r.retirementYears * 12;
  const draw =
    mPost === 0
      ? nestEgg / n
      : (nestEgg * mPost) / (1 - Math.pow(1 + mPost, -n));

  const monthlyIncome = draw + r.socialSecurityMonthly;
  const gap = r.desiredMonthly - monthlyIncome;
  const onTrack = gap <= 0;
  // additional monthly contribution required to close gap (rough): inverse FV
  let needAddl = 0;
  if (!onTrack && monthsToRetire > 0) {
    // gap in monthly draw needs extra nest egg
    const extraNeeded =
      mPost === 0
        ? gap * n
        : (gap * (1 - Math.pow(1 + mPost, -n))) / mPost;
    needAddl =
      mPre === 0
        ? extraNeeded / monthsToRetire
        : (extraNeeded * mPre) / (Math.pow(1 + mPre, monthsToRetire) - 1);
  }

  return { nestEgg, draw, monthlyIncome, gap, onTrack, needAddl };
}

function RetirementPage() {
  const [done, setDone] = useState(false);
  const [r, setR] = useState<RState>({
    currentAge: 40,
    retireAge: 65,
    currentSavings: 75000,
    monthlyContribution: 800,
    preReturn: 7,
    postReturn: 4,
    desiredMonthly: 6000,
    socialSecurityMonthly: 2200,
    retirementYears: 25,
  });
  const out = useMemo(() => project(r), [r]);

  async function onComplete() {
    const res = await logCalculatorCompletion("retirement", r, out);
    if (res.saved) toast.success("Saved to your Vault");
    setDone(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 pt-12 pb-24">
        <BackLink to="/" label="Back to home" />

        {!done ? (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12">
            <div>
              <Eyebrow>— Retirement Income —</Eyebrow>
              <h1 className="mt-5 font-display text-[36px] md:text-[42px] leading-[1.08] text-navy-900">
                Will the paycheck keep coming, after the job stops?
              </h1>
              <p className="mt-5 font-sans text-[16px] leading-[1.65] text-charcoal-900 max-w-[58ch]">
                Retirement isn&rsquo;t an age. It&rsquo;s the moment a paycheck
                arrives whether you work or not. Most families discover their
                gap five years too late. This shows yours now, while you can
                still close it.
              </p>

              <div className="mt-8 bg-white border border-bronze-600/25 rounded-[4px] p-7 shadow-soft space-y-7">
                <Section title="Today">
                  <Slider label="Current age" value={r.currentAge} min={18} max={75} onChange={(v) => setR({ ...r, currentAge: v })} />
                  <Slider label="Retire at age" value={r.retireAge} min={Math.max(r.currentAge + 1, 50)} max={80} onChange={(v) => setR({ ...r, retireAge: v })} />
                  <Money label="Current retirement savings" value={r.currentSavings} onChange={(v) => setR({ ...r, currentSavings: v })} />
                  <Money label="Monthly contribution" value={r.monthlyContribution} onChange={(v) => setR({ ...r, monthlyContribution: v })} />
                </Section>
                <Section title="In retirement">
                  <Money label="Desired monthly income" value={r.desiredMonthly} onChange={(v) => setR({ ...r, desiredMonthly: v })} />
                  <Money label="Estimated Social Security / mo" value={r.socialSecurityMonthly} onChange={(v) => setR({ ...r, socialSecurityMonthly: v })} />
                  <Slider label="Years in retirement" value={r.retirementYears} min={15} max={40} onChange={(v) => setR({ ...r, retirementYears: v })} />
                </Section>
                <div className="flex justify-end">
                  <PrimaryButton onClick={onComplete} size="lg">See the gap →</PrimaryButton>
                </div>
              </div>
            </div>

            {/* Live three-bucket sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 bg-navy-900 text-cream-50 rounded-[4px] p-6 shadow-soft">
                <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-400">
                  Projected monthly paycheck
                </p>
                <p
                  className="mt-3 font-display text-[40px] leading-none"
                  style={{
                    backgroundImage: "linear-gradient(180deg, var(--color-bronze-400), var(--color-bronze-600))",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {fmtUSD(out.monthlyIncome)}
                </p>
                <Bucket label="Social Security" amount={r.socialSecurityMonthly} total={Math.max(out.monthlyIncome, r.desiredMonthly)} />
                <Bucket label="Savings drawdown" amount={out.draw} total={Math.max(out.monthlyIncome, r.desiredMonthly)} />
                <div className="mt-6 border-t border-bronze-400/20 pt-4 font-sans text-[12px] flex items-center justify-between">
                  <span className="text-cream-50/80">Target</span>
                  <span className="font-display text-cream-50">{fmtUSD(r.desiredMonthly)}</span>
                </div>
                <div className="mt-2 font-sans text-[12px] flex items-center justify-between">
                  <span className="text-cream-50/80">Gap</span>
                  <span className={"font-display " + (out.onTrack ? "text-success" : "text-bronze-400")}>
                    {out.onTrack ? "On track" : fmtUSD(out.gap) + "/mo"}
                  </span>
                </div>
              </div>
            </aside>
          </div>
        ) : (
          <Result r={r} out={out} onRestart={() => setDone(false)} />
        )}
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-600">{title}</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

function Money({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">{label}</Label>
      <div className="mt-2 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-display text-navy-800">$</span>
        <Input
          type="number"
          min={0}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="pl-7 font-display text-[16px] text-navy-900 h-11"
        />
      </div>
    </div>
  );
}

function Slider({ label, value, min, max, onChange }: { label: string; value: number; min: number; max: number; onChange: (n: number) => void }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">{label}</Label>
      <div className="mt-2 flex items-center gap-3">
        <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-bronze-600" />
        <span className="font-display text-navy-900 text-[16px] min-w-[3ch] text-right">{value}</span>
      </div>
    </div>
  );
}

function Bucket({ label, amount, total }: { label: string; amount: number; total: number }) {
  const pct = total > 0 ? Math.min(100, (amount / total) * 100) : 0;
  return (
    <div className="mt-5">
      <div className="flex items-center justify-between font-sans text-[12px]">
        <span className="text-cream-50/80">{label}</span>
        <span className="text-bronze-400 font-semibold">{fmtUSD(amount)}</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-navy-800/60 overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, var(--color-bronze-400), var(--color-bronze-600))",
          }}
        />
      </div>
    </div>
  );
}

function Result({
  r, out, onRestart,
}: { r: RState; out: ReturnType<typeof project>; onRestart: () => void }) {
  const share = emailShareLink(
    "Our retirement income projection",
    `Currency Thoughts retirement projection:\n\n  Projected nest egg at ${r.retireAge}: ${fmtUSD(out.nestEgg)}\n  Monthly income: ${fmtUSD(out.monthlyIncome)}\n  Desired: ${fmtUSD(r.desiredMonthly)}\n  Gap: ${out.onTrack ? "On track" : fmtUSD(out.gap) + "/mo"}\n\nSee the model: https://www.currencythoughts.net/tools/retirement`,
  );
  return (
    <div className="mt-8">
      <div className="mx-auto max-w-3xl text-center">
        <Eyebrow align="center">— Your Retirement Paycheck —</Eyebrow>
        <h1
          className="mt-6 font-display leading-[1] text-[64px] md:text-[96px]"
          style={{
            backgroundImage: "linear-gradient(180deg, var(--color-bronze-400), var(--color-bronze-600))",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {fmtMonthly(out.monthlyIncome)}
        </h1>
        <p className="mt-6 font-sans text-[15px] md:text-[16px] leading-[1.7] text-charcoal-900 max-w-[60ch] mx-auto">
          {out.onTrack ? (
            <>This is what your projected savings and Social Security will produce — and it covers the life you described. The next conversation is about <em>protecting</em> it.</>
          ) : (
            <>You&rsquo;re currently {fmtUSD(out.gap)}/mo short of the life you described. An additional {fmtMonthly(out.needAddl)} starting now closes the gap — and there are protected-income strategies that close it faster.</>
          )}
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
        <Stat label={`Nest egg at ${r.retireAge}`} value={fmtUSD(out.nestEgg)} />
        <Stat label="Savings drawdown" value={fmtMonthly(out.draw)} />
        <Stat label="Social Security" value={fmtMonthly(r.socialSecurityMonthly)} />
      </div>

      <div className="mt-10 max-w-4xl mx-auto bg-navy-900 text-cream-50 rounded-[4px] p-7 shadow-soft">
        <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-400">The three buckets</p>
        <p className="mt-4 font-sans text-[14px] leading-relaxed text-cream-50/90 max-w-[60ch]">
          A resilient retirement leans on three sources: Social Security, your
          savings drawdown, and a third bucket of <em>protected income</em> that
          doesn&rsquo;t move with the market. That third bucket is where most
          families discover they&rsquo;re missing a piece.
        </p>
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
        <PrimaryButton asChild size="lg">
          <a href="/book?source=retirement">Talk to a Strategist →</a>
        </PrimaryButton>
        <SecondaryButton asChild size="lg">
          <Link to="/tools/family-snapshot">Take the Family Snapshot →</Link>
        </SecondaryButton>
      </div>

      <div className="mt-10 max-w-3xl mx-auto text-center">
        <p className="font-sans text-[13px] text-charcoal-500">
          Go deeper —{" "}
          <Link
            to="/academy/lessons/$lessonSlug"
            params={{ lessonSlug: "three-buckets-of-retirement" }}
            className="text-bronze-600 hover:text-bronze-400 underline underline-offset-4"
          >
            The three buckets of retirement income →
          </Link>
        </p>
        <div className="mt-5 flex items-center justify-center gap-3 text-[12px]">
          <a href={share} className="font-sans text-bronze-600 hover:text-bronze-400 underline underline-offset-4">Email this to my spouse</a>
          <span className="text-charcoal-500">·</span>
          <button onClick={onRestart} className="font-sans text-charcoal-500 hover:text-navy-800 underline underline-offset-4">Recalculate</button>
        </div>
      </div>

      <div className="mt-12 max-w-3xl mx-auto">
        <CalculatorDisclaimer />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-bronze-600/20 rounded-[4px] p-5 text-center shadow-soft">
      <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-charcoal-500">{label}</p>
      <p className="mt-3 font-display text-[22px] text-navy-900">{value}</p>
    </div>
  );
}